import { useEffect, useRef } from 'react';

/**
 * Arrastrar el render para alinearlo con el loteo. `unproject` lleva el
 * cursor a metros del DXF, que es donde vive el `transform`.
 *
 * `onLiveTransform` corre en el mismo evento (mover el quad).
 * `onTransformChange` se agrupa a un frame para no re-renderizar el panel
 * en cada pixel; al soltar sale sin esperar el frame. Nada de esto viaja al
 * API: el encaje se confirma con Guardar.
 *
 * @param {{
 *   mapInstanceRef: { current: import('maplibre-gl').Map|null },
 *   enabled?: boolean,
 *   pose: { dx: number, dy: number, scale: number, rotationRad: number },
 *   unproject: (lng: number, lat: number) => number[]|null,
 *   onLiveTransform?: (next: object) => void,
 *   onTransformChange?: (next: object) => void,
 * }} options
 */
export function useCadCanvasImageDrag({
  mapInstanceRef,
  enabled = false,
  pose,
  unproject,
  onLiveTransform,
  onTransformChange,
}) {
  const onTransformChangeRef = useRef(onTransformChange);
  onTransformChangeRef.current = onTransformChange;
  const onLiveTransformRef = useRef(onLiveTransform);
  onLiveTransformRef.current = onLiveTransform;
  const poseRef = useRef(pose);
  poseRef.current = pose;
  const unprojectRef = useRef(unproject);
  unprojectRef.current = unproject;

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled || !onTransformChangeRef.current) return undefined;

    const canvas = map.getCanvas();
    const dragRef = { current: null };
    let frame = 0;
    let pending = null;

    const publish = (next, final) => {
      poseRef.current = next;
      onLiveTransformRef.current?.(next);
      if (final) {
        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }
        pending = null;
        onTransformChangeRef.current?.(next);
        return;
      }
      pending = next;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!pending) return;
        onTransformChangeRef.current?.(pending);
        pending = null;
      });
    };

    const stopDrag = () => {
      if (!dragRef.current) return;
      const last = poseRef.current;
      dragRef.current = null;
      try { map.dragPan.enable(); } catch (_) { /* noop */ }
      try { canvas.style.cursor = 'grab'; } catch (_) { /* noop */ }
      publish(last, true);
    };

    const onMouseDown = (event) => {
      const lng = Number(event?.lngLat?.lng);
      const lat = Number(event?.lngLat?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      dragRef.current = { lng, lat };
      try { map.dragPan.disable(); } catch (_) { /* noop */ }
      try { canvas.style.cursor = 'grabbing'; } catch (_) { /* noop */ }
      event.preventDefault();
    };

    const onMouseMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const from = unprojectRef.current?.(drag.lng, drag.lat);
      const to = unprojectRef.current?.(event.lngLat.lng, event.lngLat.lat);
      drag.lng = event.lngLat.lng;
      drag.lat = event.lngLat.lat;
      if (!from || !to) return;
      const prev = poseRef.current;
      publish({
        ...prev,
        dx: prev.dx + (to[0] - from[0]),
        dy: prev.dy + (to[1] - from[1]),
      }, false);
    };

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', stopDrag);
    map.on('mouseout', stopDrag);
    try { canvas.style.cursor = 'grab'; } catch (_) { /* noop */ }

    return () => {
      if (frame) cancelAnimationFrame(frame);
      dragRef.current = null;
      try { map.dragPan.enable(); } catch (_) { /* noop */ }
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', stopDrag);
      map.off('mouseout', stopDrag);
      try { canvas.style.cursor = ''; } catch (_) { /* noop */ }
    };
  }, [enabled, mapInstanceRef]);
}
